import { readStaffInviteToken } from './staffInviteToken';

/** sessionStorage keys for pending parishioner invite across auth redirects. */
export const MEMBER_INVITE_TOKEN_KEY = 'pendingMemberInviteToken';
export const MEMBER_INVITE_EMAIL_KEY = 'pendingMemberInviteEmail';

export function storeMemberInviteToken(token: string) {
    if (!token) return;
    const previous = sessionStorage.getItem(MEMBER_INVITE_TOKEN_KEY);
    if (previous && previous !== token) {
        // New invite link — drop any email cached from a prior token.
        sessionStorage.removeItem(MEMBER_INVITE_EMAIL_KEY);
    }
    sessionStorage.setItem(MEMBER_INVITE_TOKEN_KEY, token);
}

export function readMemberInviteToken(): string | null {
    return sessionStorage.getItem(MEMBER_INVITE_TOKEN_KEY);
}

export function storeMemberInviteEmail(email: string) {
    if (!email) return;
    sessionStorage.setItem(MEMBER_INVITE_EMAIL_KEY, email.toLowerCase().trim());
}

export function readMemberInviteEmail(): string | null {
    return sessionStorage.getItem(MEMBER_INVITE_EMAIL_KEY);
}

export function clearMemberInviteToken() {
    sessionStorage.removeItem(MEMBER_INVITE_TOKEN_KEY);
    sessionStorage.removeItem(MEMBER_INVITE_EMAIL_KEY);
}

function responseStatus(error: unknown): number | undefined {
    return (error as { response?: { status?: number } })?.response?.status;
}

/** Wrong authenticated email — keep token so the user can switch accounts. */
export function isWrongAccountMemberInviteError(error: unknown): boolean {
    return responseStatus(error) === 403;
}

/** Session missing/expired — keep invite token; send user through sign-in again. */
export function isAuthRequiredMemberInviteError(error: unknown): boolean {
    return responseStatus(error) === 401;
}

/**
 * Terminal invite outcomes — clear the pending token so later logins are not
 * bounced to a dead accept page. Does NOT include network/5xx/timeouts (no or
 * 5xx status), 401, or 403 wrong-account.
 */
export function isTerminalMemberInviteError(error: unknown): boolean {
    const status = responseStatus(error);
    if (status === undefined) return false; // network / transport / timeout
    if (status === 404 || status === 409 || status === 410) return true;
    if (status === 400) return true; // invalid token shape / validation
    return false;
}

/**
 * After login/2FA, prefer finishing a pending invite over going home.
 * Staff invite takes precedence when both tokens somehow exist.
 */
export function postAuthPath(homePath: string, memberAcceptPath: string, staffAcceptPath?: string): string {
    if (staffAcceptPath && readStaffInviteToken()) return staffAcceptPath;
    return readMemberInviteToken() ? memberAcceptPath : homePath;
}
